import { handleActions } from 'redux-actions';
import { defaultNotificationsWidgetState } from './defaults';
import { NotificationsWidgetData } from './types';
import { initNotificationsPage } from '../../actions';
import {
  makeSpecificSaveableWidgetDataMergeFunction,
  INITIAL_SAVE_REQUEST_META,
  saveableWidgetRequestMetaReducer,
} from '../utils';
import {
  setNotificationsWidgetDeviantArtMentions,
  setNotificationsWidgetDeviantArtBadges,
  setNotificationsWidgetDeviantArtNewMembers,
  setNotificationsWidgetEmailAccountActivity,
  setNotificationsWidgetEmailNews,
  setNotificationsWidgetEmailPasswordReset,
  setNotificationsWidgetDeviantArtProtect,
  saveNotificationsFailure,
  saveNotificationsSuccess,
  saveNotifications,
} from './actions';

const mergeNotificationsWidgetDataChange =
  makeSpecificSaveableWidgetDataMergeFunction(defaultNotificationsWidgetState);

const mergeDeviantArtGroupDataChange = (
  currentState: NotificationsWidgetData['currentData']['deviantart'],
  data: Partial<NotificationsWidgetData['currentData']['deviantart']>
): NonNullable<NotificationsWidgetData['currentData']['deviantart']> => {
  return {
    ...defaultNotificationsWidgetState.currentData.deviantart,
    ...currentState,
    ...data,
  };
};

const mergeEmailGroupDataChange = (
  currentState: NotificationsWidgetData['currentData']['email'],
  data: Partial<NotificationsWidgetData['currentData']['email']>
): NonNullable<NotificationsWidgetData['currentData']['email']> => {
  return {
    ...defaultNotificationsWidgetState.currentData.email,
    ...currentState,
    ...data,
  };
};

export default handleActions<NotificationsWidgetData, any>(
  {
    [initNotificationsPage.toString()]: (
      state,
      action: ReturnType<typeof initNotificationsPage>
    ) => {
      const data: NotificationsWidgetData['initialData'] = {
        deviantart: action.payload.data.deviantart,
        email: action.payload.data.email,
      };
      return {
        initialData: data,
        currentData: data,
        isDirty: false,
        saveRequest: INITIAL_SAVE_REQUEST_META,
      };
    },
    [setNotificationsWidgetDeviantArtMentions.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetDeviantArtMentions>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        deviantart: mergeDeviantArtGroupDataChange(
          state.currentData.deviantart,
          {
            mentions: action.payload.mentions,
          }
        ),
      }),
    [setNotificationsWidgetDeviantArtBadges.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetDeviantArtBadges>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        deviantart: mergeDeviantArtGroupDataChange(
          state.currentData.deviantart,
          {
            badges: action.payload.badges,
          }
        ),
      }),
    [setNotificationsWidgetDeviantArtNewMembers.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetDeviantArtNewMembers>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        deviantart: mergeDeviantArtGroupDataChange(
          state.currentData.deviantart,
          {
            newMembers: action.payload.newMembers,
          }
        ),
      }),
    [setNotificationsWidgetDeviantArtProtect.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetDeviantArtProtect>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        deviantart: mergeDeviantArtGroupDataChange(
          state.currentData.deviantart,
          {
            deviantartProtect: action.payload.protect,
          }
        ),
      }),
    [setNotificationsWidgetEmailAccountActivity.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetEmailAccountActivity>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        email: mergeEmailGroupDataChange(state.currentData.email, {
          accountActivity: action.payload.accountActivity,
        }),
      }),
    [setNotificationsWidgetEmailNews.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetEmailNews>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        email: mergeEmailGroupDataChange(state.currentData.email, {
          news: action.payload.news,
        }),
      }),
    [setNotificationsWidgetEmailPasswordReset.toString()]: (
      state,
      action: ReturnType<typeof setNotificationsWidgetEmailPasswordReset>
    ) =>
      mergeNotificationsWidgetDataChange(state, {
        email: mergeEmailGroupDataChange(state.currentData.email, {
          passwordReset: action.payload.passwordReset,
        }),
      }),
    ...saveableWidgetRequestMetaReducer({
      initialAction: saveNotifications,
      successAction: saveNotificationsSuccess,
      failureAction: saveNotificationsFailure,
    }),
  },
  defaultNotificationsWidgetState
);
